/*
 * Settings → Operations → Well Servicing (WS-1, fieldiq-api#1269).
 *
 * Two sections:
 *
 *  - The company defaults (GET / PUT /well-servicing/settings): the default
 *    ticket mode, the tour pattern, the tour start time (the tenant's local
 *    wall clock, HH:MM), the default tour length, and the standby minimum
 *    (blank = no minimum stated).
 *  - One customer's settings (GET / PUT /well-servicing/customers/:id/settings):
 *    its own ticket mode or "Company default", and who signs its tickets.
 *
 * A tenant (or customer) with no row reads the API's defaults with
 * `configured: false`, and the page says so. Only changed fields are sent.
 * Writes are admin / office staff (WS_ADMIN); everyone else reads with the
 * controls disabled. A 403 is said as a refusal, never as a saved toast.
 */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  useUpdateWsCustomerSettings,
  useUpdateWsSettings,
  useWsCustomers,
  useWsCustomerSettings,
  useWsMeta,
  useWsSettings,
} from '../../hooks/useWellServicing';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { blankToNull, canAdminWs, catalogLabel, toInput, wsRefusal, WS_TIME_PATTERN } from '../../lib/well-servicing';
import type { WsCatalog, WsCustomerSettings, WsCustomerSettingsUpdate, WsSettings, WsSettingsUpdate } from '../../types/well-servicing';

const inputCls =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50 disabled:opacity-60 disabled:cursor-not-allowed';
const btnPrimary = 'flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed';

function ErrorLine({ message, testId }: { message: string | null; testId?: string }) {
  if (!message) return null;
  return (
    <p role="alert" data-testid={testId} className="rounded-lg border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
      {message}
    </p>
  );
}

function Field({ id, label, hint, children }: { id: string; label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium text-white block mb-1">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-fieldiq-muted mt-1">{hint}</p>}
    </div>
  );
}

/** Hours: > 0 and at most 24 (mirrors the API). Returns the number, or a sentence. */
function parseHours(text: string, label: string): number | string {
  const t = text.trim();
  const n = Number(t);
  if (t === '' || !/^\d+(\.\d+)?$/.test(t) || !Number.isFinite(n) || n <= 0 || n > 24) {
    return `${label} must be a number of hours greater than 0 and at most 24, e.g. 12.`;
  }
  return n;
}

/** The options a catalog select offers; the stored value stays selectable when the catalog lacks it. */
function options(list: { value: string; label: string }[] | undefined, current: string): { value: string; label: string }[] {
  const entries = list ?? [];
  return current && !entries.some((e) => e.value === current) ? [{ value: current, label: current }, ...entries] : entries;
}

function CompanyDefaults({ settings, catalog, canWrite }: { settings: WsSettings; catalog: WsCatalog | null; canWrite: boolean }) {
  const save = useUpdateWsSettings();
  const initial = {
    mode: settings.defaultTicketMode ?? '',
    pattern: settings.tourPattern ?? '',
    start: settings.tourStartTime ?? '',
    hours: toInput(settings.defaultTourHours),
    standby: toInput(settings.standbyMinimumHours),
  };
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setForm(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = (Object.keys(form) as (keyof typeof form)[]).some((k) => form[k].trim() !== initial[k]);

  const onSave = () => {
    setError(null);
    const body: WsSettingsUpdate = {};
    if (form.mode && form.mode !== initial.mode) body.default_ticket_mode = form.mode;
    if (form.pattern && form.pattern !== initial.pattern) body.tour_pattern = form.pattern;
    if (form.start.trim() !== initial.start) {
      if (!WS_TIME_PATTERN.test(form.start.trim())) return setError('The tour start time must be a 24-hour time, e.g. 07:00.');
      body.tour_start_time = form.start.trim();
    }
    if (form.hours.trim() !== initial.hours) {
      const h = parseHours(form.hours, 'The default tour length');
      if (typeof h === 'string') return setError(h);
      body.default_tour_hours = h;
    }
    if (form.standby.trim() !== initial.standby) {
      if (form.standby.trim() === '') body.standby_minimum_hours = null;
      else {
        const h = parseHours(form.standby, 'The standby minimum');
        if (typeof h === 'string') return setError(h);
        body.standby_minimum_hours = h;
      }
    }
    if (Object.keys(body).length === 0) return setError('Nothing has changed.');
    save.mutate(body, {
      onSuccess: () => toast.success('Well servicing settings saved'),
      onError: (err) => setError(wsRefusal(err, 'The settings could not be saved.')),
    });
  };

  return (
    <section className="space-y-3" data-testid="ws-company-settings">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-bold text-white">Company defaults</h2>
        {canWrite && (
          <button type="button" onClick={onSave} disabled={!dirty || save.isPending} className={btnPrimary}>
            {save.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
            {save.isPending ? 'Saving...' : 'Save'}
          </button>
        )}
      </div>
      {!settings.configured && (
        <p className="text-xs text-fieldiq-muted" data-testid="ws-settings-defaults">
          These are the defaults. Nobody has saved well servicing settings for this company yet.
        </p>
      )}
      <ErrorLine message={error} testId="ws-settings-error" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field id="ws-ticket-mode" label="Default ticket mode" hint="Used when a customer has no setting of its own.">
          <select id="ws-ticket-mode" className={inputCls} value={form.mode} disabled={!canWrite} onChange={(e) => set('mode', e.target.value)}>
            {!form.mode && <option value="">—</option>}
            {options(catalog?.ticketModes, form.mode).map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </Field>
        <Field id="ws-tour-pattern" label="Tour pattern">
          <select id="ws-tour-pattern" className={inputCls} value={form.pattern} disabled={!canWrite} onChange={(e) => set('pattern', e.target.value)}>
            {!form.pattern && <option value="">—</option>}
            {options(catalog?.tourPatterns, form.pattern).map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </Field>
        <Field id="ws-tour-start" label="Tour start time" hint="24-hour, the company's local time, e.g. 07:00.">
          <input id="ws-tour-start" className={inputCls} value={form.start} placeholder="07:00" disabled={!canWrite} onChange={(e) => set('start', e.target.value)} />
        </Field>
        <Field id="ws-tour-hours" label="Default tour length (hours)" hint="More than 0, at most 24.">
          <input id="ws-tour-hours" className={inputCls} inputMode="decimal" value={form.hours} disabled={!canWrite} onChange={(e) => set('hours', e.target.value)} />
        </Field>
        <Field id="ws-standby-min" label="Standby minimum (hours)" hint="Blank when no minimum standby charge applies.">
          <input id="ws-standby-min" className={inputCls} inputMode="decimal" value={form.standby} disabled={!canWrite} onChange={(e) => set('standby', e.target.value)} />
        </Field>
      </div>
    </section>
  );
}

function CustomerSettingsForm({
  cs,
  catalog,
  companyMode,
  canWrite,
}: {
  cs: WsCustomerSettings;
  catalog: WsCatalog | null;
  companyMode: string | null;
  canWrite: boolean;
}) {
  const save = useUpdateWsCustomerSettings();
  const initial = { mode: cs.ticketMode ?? '', name: cs.signerName ?? '', title: cs.signerTitle ?? '' };
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setForm(initial);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cs]);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = form.mode !== initial.mode || form.name.trim() !== initial.name || form.title.trim() !== initial.title;

  const onSave = () => {
    setError(null);
    const body: WsCustomerSettingsUpdate = {};
    // An empty choice is "Company default": sent as null, never as "".
    if (form.mode !== initial.mode) body.ticket_mode = form.mode || null;
    if (form.name.trim() !== initial.name) body.signer_name = blankToNull(form.name);
    if (form.title.trim() !== initial.title) body.signer_title = blankToNull(form.title);
    if (Object.keys(body).length === 0) return setError('Nothing has changed.');
    save.mutate(
      { customerId: cs.customerId, body },
      {
        onSuccess: () => toast.success(`Settings saved for ${cs.customerName ?? 'the customer'}`),
        onError: (err) => setError(wsRefusal(err, 'The customer settings could not be saved.')),
      },
    );
  };

  const defaultLabel = `Company default (${catalogLabel(catalog?.ticketModes, companyMode)})`;

  return (
    <div className="space-y-3" data-testid="ws-customer-settings-form">
      {!cs.configured && (
        <p className="text-xs text-fieldiq-muted">Nothing saved for this customer yet: the company default applies.</p>
      )}
      <ErrorLine message={error} testId="ws-customer-settings-error" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field
          id="ws-cust-ticket-mode"
          label="Ticket mode"
          hint={`Applies now: ${catalogLabel(catalog?.ticketModes, cs.effectiveTicketMode)}`}
        >
          <select id="ws-cust-ticket-mode" className={inputCls} value={form.mode} disabled={!canWrite} onChange={(e) => set('mode', e.target.value)}>
            <option value="">{defaultLabel}</option>
            {options(catalog?.ticketModes, form.mode).map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </Field>
        <Field id="ws-cust-signer-name" label="Signer name" hint="Who signs this customer's tickets. Blank when not known.">
          <input id="ws-cust-signer-name" className={inputCls} maxLength={200} value={form.name} disabled={!canWrite} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field id="ws-cust-signer-title" label="Signer title" hint="e.g. Company man, Wellsite supervisor.">
          <input id="ws-cust-signer-title" className={inputCls} maxLength={100} value={form.title} disabled={!canWrite} onChange={(e) => set('title', e.target.value)} />
        </Field>
      </div>
      {canWrite && (
        <div className="flex justify-end">
          <button type="button" onClick={onSave} disabled={!dirty || save.isPending} className={btnPrimary}>
            {save.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
            {save.isPending ? 'Saving...' : 'Save customer'}
          </button>
        </div>
      )}
    </div>
  );
}

function CustomerSettings({ catalog, companyMode, canWrite }: { catalog: WsCatalog | null; companyMode: string | null; canWrite: boolean }) {
  const customers = useWsCustomers();
  const [customerId, setCustomerId] = useState('');
  const cs = useWsCustomerSettings(customerId || undefined);
  const customersFailure = queryLoadFailure(customers);
  const csFailure = queryLoadFailure(cs);

  return (
    <section className="space-y-3 border-t border-fieldiq-border pt-4" data-testid="ws-customer-settings">
      <h2 className="text-base font-bold text-white">Customer settings</h2>
      <p className="text-xs text-fieldiq-muted">A customer may take its own ticket mode and name who signs its tickets.</p>
      {customers.isLoading ? (
        <Loader2 className="w-5 h-5 text-fieldiq-accent animate-spin" aria-label="Loading customers" />
      ) : customersFailure ? (
        <LoadFailureNotice error={queryFailureError(customers)} kind={customersFailure} subject="your customers" />
      ) : (customers.data ?? []).length === 0 ? (
        <p className="text-sm text-fieldiq-text">There are no customers yet. Add one under Clients first.</p>
      ) : (
        <>
          <div className="max-w-sm">
            <Field id="ws-cust-picker" label="Customer">
              <select id="ws-cust-picker" className={inputCls} value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                <option value="">Select a customer…</option>
                {(customers.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {!customerId ? null : cs.isLoading ? (
            <Loader2 className="w-5 h-5 text-fieldiq-accent animate-spin" aria-label="Loading customer settings" />
          ) : csFailure || !cs.data?.data ? (
            <LoadFailureNotice error={queryFailureError(cs)} kind={csFailure ?? 'unreachable'} subject="this customer's settings" />
          ) : (
            <CustomerSettingsForm key={customerId} cs={cs.data.data} catalog={catalog} companyMode={companyMode} canWrite={canWrite} />
          )}
        </>
      )}
    </section>
  );
}

export default function WellServicingSettingsPage() {
  const { user } = useAuth();
  const canWrite = canAdminWs(user?.role);
  const query = useWsSettings();
  const catalog = useWsMeta().data?.data ?? null;
  const settings = query.data?.data ?? null;
  const failure = queryLoadFailure(query);

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" aria-label="Loading well servicing settings" />
      </div>
    );
  }
  if (failure || !settings) {
    return (
      <div className="px-6 py-4">
        <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="well servicing settings" />
      </div>
    );
  }

  return (
    <div className="min-h-full" data-testid="ws-settings-page">
      <div className="px-6 py-4 space-y-4">
        {!canWrite && (
          <p className="text-xs text-fieldiq-muted" data-testid="ws-settings-readonly">
            Only an admin or office staff can change well servicing settings.
          </p>
        )}
        <CompanyDefaults settings={settings} catalog={catalog} canWrite={canWrite} />
        <CustomerSettings catalog={catalog} companyMode={settings.defaultTicketMode} canWrite={canWrite} />
      </div>
    </div>
  );
}
